/**
 * NotificationBell
 * ────────────────
 * Bell icon met unread-badge + dropdown panel.
 * Polled elke 90s; opent dropdown bij klik. Per notificatie:
 *   - 1-klik actie-knop (bv. opent AddNewContactsModal)
 *   - "Markeer gelezen" en "Verberg" acties
 *
 * Profiel-scoped: alleen meldingen van actief profiel.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Bell, X, Check, ArrowRight } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import AddNewContactsModal from '../marketing/AddNewContactsModal';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL = 90_000;

const timeAgo = (iso) => {
  if (!iso) return '';
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const m = Math.floor(ms / 60000);
    if (m < 1) return 'zojuist';
    if (m < 60) return `${m} min geleden`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h} uur geleden`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d} dag${d === 1 ? '' : 'en'} geleden`;
    return new Date(iso).toLocaleDateString('nl-NL', { day: '2-digit', month: 'short' });
  } catch { return ''; }
};

export default function NotificationBell({ activeProfile }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [actionCampaign, setActionCampaign] = useState(null);
  const containerRef = useRef(null);
  const navigate = useNavigate();

  const fetchUnread = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/notifications?limit=10`, { credentials: 'include' });
      if (r.ok) {
        const d = await r.json();
        setItems(d.items || []);
        setUnread(d.unread || 0);
      }
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetchUnread();
    const id = setInterval(fetchUnread, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [fetchUnread, activeProfile]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('touchstart', onDoc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('touchstart', onDoc);
    };
  }, [open]);

  const markRead = async (nid) => {
    try {
      await fetch(`${API}/api/notifications/${nid}/read`, {
        method: 'POST', credentials: 'include',
      });
      await fetchUnread();
    } catch { /* silent */ }
  };

  const dismiss = async (nid) => {
    try {
      await fetch(`${API}/api/notifications/${nid}/dismiss`, {
        method: 'POST', credentials: 'include',
      });
      await fetchUnread();
    } catch { /* silent */ }
  };

  const readAll = async () => {
    try {
      await fetch(`${API}/api/notifications/read-all`, {
        method: 'POST', credentials: 'include',
      });
      await fetchUnread();
    } catch { /* silent */ }
  };

  const runAction = async (notif) => {
    if (!notif.action) return;
    await markRead(notif.notification_id);
    const kind = notif.action.kind;
    if (kind === 'add_to_campaign' && notif.action.campaign_id) {
      setActionCampaign({
        campaign_id: notif.action.campaign_id,
        name: notif.action.campaign_name || 'campagne',
      });
      setOpen(false);
    } else if (kind === 'open_campaign_queue' && notif.action.campaign_id) {
      // Stuur de gebruiker naar campagnes-pagina; queue-knop staat naast de campagne
      setOpen(false);
      navigate('/email/campaigns');
    } else if (kind === 'open_global_inbox') {
      setOpen(false);
      navigate('/global-inbox');
    } else if (kind === 'open_facilities_page') {
      setOpen(false);
      navigate('/facilities');
    } else if (kind === 'open_campaigns_page') {
      setOpen(false);
      navigate('/email/campaigns');
    }
  };

  return (
    <>
      <div ref={containerRef} className="relative">
        <button
          onClick={() => setOpen((v) => !v)}
          data-testid="notification-bell"
          className="relative p-2 hover:bg-zinc-100 active:bg-zinc-200 rounded-sm transition-colors"
          title={unread > 0 ? `${unread} ongelezen meldingen` : 'Meldingen'}
        >
          <Bell size={18} weight={unread > 0 ? 'fill' : 'regular'} className={unread > 0 ? 'text-amber-600' : 'text-zinc-600'} />
          {unread > 0 && (
            <span
              data-testid="notification-bell-badge"
              className="absolute -top-0.5 -right-0.5 bg-red-600 text-white text-[9px] font-bold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1"
            >
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>

        {open && (
          <div
            data-testid="notification-dropdown"
            className="fixed left-2 right-2 sm:left-auto sm:right-auto sm:absolute sm:top-0 sm:left-full sm:ml-2 top-14 sm:top-0 sm:w-[380px] max-h-[80vh] bg-white border border-zinc-200 rounded-sm shadow-2xl overflow-hidden flex flex-col z-50"
          >
            <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between bg-zinc-50">
              <h3 className="text-sm font-bold text-zinc-900">Meldingen</h3>
              {unread > 0 && (
                <button
                  onClick={readAll}
                  data-testid="notification-read-all"
                  className="text-[11px] text-indigo-600 hover:text-indigo-800 active:text-indigo-900"
                >
                  Alles markeren als gelezen
                </button>
              )}
            </div>

            <div className="flex-1 overflow-y-auto">
              {items.length === 0 && (
                <div className="text-center text-sm text-zinc-400 py-12 italic" data-testid="notifications-empty">
                  Geen meldingen
                </div>
              )}
              {items.map((n) => {
                const isUnread = n.status === 'unread';
                return (
                  <div
                    key={n.notification_id}
                    data-testid={`notification-row-${n.notification_id}`}
                    className={`px-4 py-3 border-b border-zinc-100 hover:bg-zinc-50 ${isUnread ? 'bg-amber-50/40' : ''}`}
                  >
                    <div className="flex items-start gap-2 mb-2">
                      {isUnread && <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-1.5 flex-shrink-0"></span>}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-zinc-900 leading-snug">{n.title}</div>
                        <div className="text-[11px] text-zinc-500 mt-0.5">{timeAgo(n.created_at)}</div>
                      </div>
                      <button
                        onClick={() => dismiss(n.notification_id)}
                        data-testid={`notification-dismiss-${n.notification_id}`}
                        className="p-1.5 -mt-1 -mr-1 hover:bg-zinc-200 active:bg-zinc-300 rounded-sm text-zinc-400 hover:text-zinc-700"
                        title="Verberg"
                      >
                        <X size={12} />
                      </button>
                    </div>
                    <p className="text-xs text-zinc-700 leading-relaxed mb-2.5">{n.body}</p>
                    <div className="flex flex-wrap gap-2">
                      {n.action && (
                        <button
                          onClick={() => runAction(n)}
                          data-testid={`notification-action-${n.notification_id}`}
                          className="px-3 py-1.5 text-[11px] font-bold bg-emerald-600 text-white hover:bg-emerald-700 active:bg-emerald-800 rounded-sm flex items-center gap-1.5 transition-colors"
                        >
                          {n.action.label || 'Open'}
                          <ArrowRight size={11} weight="bold" />
                        </button>
                      )}
                      {isUnread && (
                        <button
                          onClick={() => markRead(n.notification_id)}
                          data-testid={`notification-read-${n.notification_id}`}
                          className="px-2.5 py-1.5 text-[11px] font-medium border border-zinc-300 bg-white text-zinc-600 hover:bg-zinc-100 active:bg-zinc-200 rounded-sm flex items-center gap-1 transition-colors"
                        >
                          <Check size={11} /> Gelezen
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {actionCampaign && (
        <AddNewContactsModal
          campaign={actionCampaign}
          onClose={() => { setActionCampaign(null); fetchUnread(); }}
          onDone={() => { /* keep modal open until user closes via 'Sluiten' */ }}
        />
      )}
    </>
  );
}
